import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { InlinePointsEditor } from './InlinePointsEditor';
import { getQuestionTypeLabel } from './assessmentResultUtils';

interface AssessmentQuestionReviewProps {
    question: any;
    index: number;
    assessmentResultId: string;
    isEditable?: boolean;
}

export function AssessmentQuestionReview({ 
    question: q, 
    index, 
    assessmentResultId,
    isEditable = false
}: AssessmentQuestionReviewProps) {
    if (!q.e177_Image) {
        delete q["e177_Image"];
    }
    if (q.e177_RequiresManualMarking) {
        delete q["e177_CorrectAnswer"];
    }

    const questionTypeLabel = getQuestionTypeLabel(q.e177_QuestionTypeKey);
    // Only show skipped badge if explicitly true (bool? field, defaults to false)
    const wasSkipped = q.e177_WasSkipped === true;
    const maxPoints = q.e177_PointsPossible ?? q.e177_MaxPoints ?? 0;

    return (
        <Card className="border-l-4 border-l-action-blue">
            <CardHeader>
                <div className="flex items-start justify-between gap-4">
                    <CardTitle className="text-base flex-1">
                        {index + 1}. {q.e177_QuestionText}
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {wasSkipped && (
                            <Badge className="bg-orange-100 text-orange-800">Skipped</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                            {questionTypeLabel}
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Question Image */}
                {q.e177_Image && (
                    <div className="flex justify-center">
                        <img 
                            src={q.e177_Image} 
                            alt={q.e177_ImageCaption || 'Question image'} 
                            className="max-w-full h-auto rounded-lg"
                        />
                    </div>
                )}

                {/* Question Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-medium text-gray-500">Student Answer</label>
                        <p className="text-gray-900 mt-1">{q.e177_StudentAnswer || 'No answer provided'}</p>
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium text-gray-500">Correct Answer</label>
                        <p className="text-gray-900 mt-1">{q.e177_CorrectAnswer || 'N/A'}</p>
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium text-gray-500">
                            Points
                            {isEditable && !wasSkipped && (
                                <span className="ml-2 text-xs text-action-blue font-normal">(click to edit)</span>
                            )}
                        </label>
                        <div className="flex items-center space-x-2 mt-1">
                            <InlinePointsEditor
                                pointsGiven={q.e177_PointsGiven}
                                maxPoints={maxPoints}
                                assessmentResultId={assessmentResultId}
                                questionId={q.e177_QuestionId}
                                isEditable={isEditable}
                                wasSkipped={wasSkipped}
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium text-gray-500">Question Type</label>
                        <p className="text-gray-900 mt-1">{questionTypeLabel}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}





